'use client';

import { usePathname } from 'next/navigation';
import { RefreshCw, Moon, Sun, ChevronRight, Shield } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useStatus } from '@/lib/hooks/use-status';
import { useAuthStore } from '@/lib/auth-store';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { useI18n } from '@/lib/i18n';

const ROLLEN_FARBEN: Record<string, string> = {
  admin: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  einkauf: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  qualitaet: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  logistik: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

const ROLLEN_LABEL: Record<string, string> = {
  admin: 'Admin',
  einkauf: 'Einkauf',
  qualitaet: 'Qualität',
  logistik: 'Logistik',
};

const PAGE_TITLES: Record<string, { title: string; subtitle: string; breadcrumb: string }> = {
  '/bestellungen': {
    title: 'Auftragsbestätigungen',
    subtitle: 'Übersicht aller verarbeiteten Bestellungen',
    breadcrumb: 'Bestellungen',
  },
  '/eskalationen': {
    title: 'Eskalationen',
    subtitle: 'Abweichungen und offene Aufgaben',
    breadcrumb: 'Eskalationen',
  },
  '/unklarheiten': {
    title: 'Unklarheiten',
    subtitle: 'Nicht zugeordnete E-Mails',
    breadcrumb: 'Unklarheiten',
  },
  '/aktivitaet': {
    title: 'Agent-Aktivität',
    subtitle: 'Automatische Aktionen',
    breadcrumb: 'Aktivität',
  },
  '/steuerung': {
    title: 'Steuerung',
    subtitle: 'Agent steuern und Konfiguration',
    breadcrumb: 'Steuerung',
  },
};

export function Header() {
  const { t } = useI18n();
  const pathname = usePathname();
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const { data: status } = useStatus();
  const { benutzer } = useAuthStore();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const page = PAGE_TITLES[pathname] ?? {
    title: 'Dashboard',
    subtitle: 'KI-gestützte Lieferterminverfolgung',
    breadcrumb: 'Dashboard',
  };

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-card px-6">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>{t('Dashboard')}</span>
          <ChevronRight className="h-3 w-3" />
          <span className="font-medium text-foreground">{t(page.breadcrumb)}</span>
        </div>

        <div
          className={cn(
            'ml-3 flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-[10px] font-medium text-muted-foreground',
          )}
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              status?.agent_laeuft ? 'bg-emerald-400' : 'bg-red-400'
            )}
          />
          {status?.agent_laeuft ? t('Aktiv') : t('Gestoppt')}
        </div>
      </div>

      <div className="flex items-center gap-1">
        {/* Rollen-Badge */}
        {benutzer && (
          <div
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-medium',
              ROLLEN_FARBEN[benutzer.rolle] || 'bg-muted text-muted-foreground border-border',
            )}
          >
            <Shield className="h-3 w-3" />
            {t(ROLLEN_LABEL[benutzer.rolle] || benutzer.rolle)}
          </div>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['status'] });
            queryClient.invalidateQueries({ queryKey: ['logs'] });
          }}
          title={t('Daten aktualisieren')}
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          title={t('Theme wechseln')}
        >
          {mounted && theme === 'dark' ? (
            <Sun className="h-3.5 w-3.5" />
          ) : (
            <Moon className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
    </header>
  );
}
